const GENERIC_TAGS = new Set([
  'div',
  'span',
  'section',
  'main',
  'header',
  'footer',
  'nav',
  'article',
  'aside',
  'p',
  'li',
  'ul',
  'ol',
]);
const MAX_CLASSES = 3;
const MAX_ANCESTOR_DEPTH = 5;
const MAX_ARIA_LABEL_LENGTH = 40;
const MAX_COMPONENT_NAME_DEPTH = 100;

function getAttr(el: Element, attr: string): string {
  return el.getAttribute(attr) || '';
}

function buildSelfSelector(el: Element): string {
  const tag = el.tagName?.toLowerCase?.();
  if (!tag) {
    return '';
  }
  let selector = tag;

  if (el.id) {
    selector += `#${el.id}`;
  }

  const classNames = getAttr(el, 'class');
  if (classNames.trim()) {
    const classes = classNames.trim().split(/\s+/);
    selector += `.${classes.slice(0, MAX_CLASSES).join('.')}`;
    if (classes.length > MAX_CLASSES) {
      selector += '...';
    }
  }

  const testId = getAttr(el, 'data-test-id');
  if (testId) {
    selector += `[data-test-id="${testId}"]`;
  }

  const role = getAttr(el, 'role');
  if (role && role !== 'presentation' && role !== 'none') {
    selector += `[role="${role}"]`;
  }

  const ariaLabel = getAttr(el, 'aria-label');
  if (ariaLabel) {
    const label =
      ariaLabel.length > MAX_ARIA_LABEL_LENGTH
        ? `${ariaLabel.slice(0, MAX_ARIA_LABEL_LENGTH)}...`
        : ariaLabel;
    selector += `[aria-label="${label}"]`;
  }

  if (['input', 'textarea', 'select'].includes(tag)) {
    const name = getAttr(el, 'name');
    if (name) {
      selector += `[name="${name}"]`;
    }
  }

  if (tag === 'img') {
    const alt = getAttr(el, 'alt');
    if (alt) {
      const truncated =
        alt.length > MAX_ARIA_LABEL_LENGTH
          ? `${alt.slice(0, MAX_ARIA_LABEL_LENGTH)}...`
          : alt;
      selector += `[alt="${truncated}"]`;
    }
  }

  return selector;
}

function isDescriptive(selector: string, tag: string): boolean {
  if (!selector) {
    return false;
  }
  return selector !== tag || !GENERIC_TAGS.has(tag);
}

function buildAncestorPrefix(el: Element): string {
  const tag = el.tagName?.toLowerCase?.();
  if (!tag) {
    return '';
  }
  let prefix = tag;
  if (el.id) {
    prefix += `#${el.id}`;
  }
  const testId = getAttr(el, 'data-test-id');
  if (testId) {
    prefix += `[data-test-id="${testId}"]`;
  }
  const idMarker = getAttr(el, 'data-id-marker');
  if (idMarker) {
    prefix += `[data-id-marker="${idMarker}"]`;
  }
  if (!testId && !idMarker) {
    const classNames = getAttr(el, 'class');
    if (classNames.trim()) {
      prefix += `.${classNames.trim().split(/\s+/)[0]}`;
    }
  }
  return prefix;
}

export function getElementSelector(node: Node | EventTarget | null): string {
  if (!node || !('tagName' in node)) {
    return '';
  }
  const el = node as Element;

  const selfSelector = buildSelfSelector(el);

  let ancestorPart = '';
  let current: Element | null = el.parentElement;
  let depth = 0;
  while (current && depth < MAX_ANCESTOR_DEPTH) {
    depth++;
    const ancestorPrefix = buildAncestorPrefix(current);
    const ancestorTag = current.tagName?.toLowerCase?.() ?? '';
    if (isDescriptive(ancestorPrefix, ancestorTag)) {
      const separator = depth === 1 ? ' > ' : ' .. ';
      ancestorPart = ancestorPrefix + separator;
      break;
    }
    current = current.parentElement;
  }

  return ancestorPart + selfSelector;
}

export function getNearestComponentName(
  node: Node | EventTarget | null
): string {
  if (!node) {
    return '';
  }
  let current: Element | null =
    'tagName' in node ? (node as Element) : (node as Node).parentElement;
  let depth = 0;
  while (current && depth < MAX_COMPONENT_NAME_DEPTH) {
    const name = current.getAttribute('data-component-name');
    if (name) {
      return name;
    }
    current = current.parentElement;
    depth++;
  }
  return '';
}
